import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Alert,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ConfirmationDialog from '@components/ConfirmationDialog';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';
import { Box } from '@mui/system';
import { FORM_ERROR } from 'final-form';
import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';
import {
  deleteTogglez,
  getTogglez,
  updateTogglez,
  addTogglez,
} from '../store/toggles';
import ApplicationPageTemplate from '../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import TableToolbar from '../../../components/TableToolbar';
import { viewToggles } from '../payment-configuration.breadcrumbs';

function ActionToolbar({ permissions, onCreate }) {
  return (
    <TableToolbar>
      <Button
        disabled={!permissions?.canCreateTogglz}
        variant="contained"
        onClick={onCreate}
        startIcon={<AddIcon />}>
        New
      </Button>
    </TableToolbar>
  );
}

export default function ToggleList({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const toggles = useSelector((state) => state.toggles.data);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false });
  const [toDeleteToggle, setToDeleteToggle] = useState('');
  const [newToggleDialogOpen, setNewToggleDialogOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    if (!permissions?.canViewTogglz) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewToggles));
  }, [dispatch]);

  const deleteHandler = (id) => {
    setToDeleteToggle(id);
    setShowDeleteModal({ show: true });
  };

  const yesDeleteHandler = (id) => {
    dispatch(deleteTogglez({ toggleId: id })).then((data) => {
      if (data.payload && data.payload.status === 'OK') {
        dispatch(showSuccessMessage('Toggle Deleted Successfully'));
      }
    });
  };

  useEffect(() => {
     setIsFetchingData(true);
     dispatch(getTogglez()).then(() => {
       setIsFetchingData(false);
     });
  }, [dispatch]);

  const statusChangeHandler = useMemo(
    () => (event, toggle) => {
      const toggleData = {
        ...toggle,
        status: event.target.checked ? 'ENABLED' : 'DISABLED',
      };
      dispatch(
        updateTogglez({
          formValues: { ...toggleData },
        }),
      ).then((data) => {
        if (data.payload && data.payload.data && data.payload.status === 'OK') {
          dispatch(showSuccessMessage('Toggles Status Updated Successfully'));
        } else dispatch(showErrorMessage('Failed to update toggle status'));
      });
    },
    [dispatch],
  );

  const createNewToggle = (values) =>
    dispatch(
      addTogglez({
        payload: {
          featureName: values.featureName,
          status: 'NEW',
        },
      }),
      // eslint-disable-next-line consistent-return
    ).then((resp) => {
      if (
        resp.payload &&
        resp.payload.data &&
        resp.payload.status === 'Created'
      ) {
        dispatch(showSuccessMessage('Toggle Added Successfully'));
        setNewToggleDialogOpen(false);
      } else {
        return { [FORM_ERROR]: resp.payload.message };
      }
    });

  const validateNewToggle = (values) => {
    if (!values.featureName) {
      return { featureName: 'Feature Name is mandatory and cannot be empty' };
    }
    return {};
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title="Toggles"
      description="List of the configured toggles in the system, you can use the page to add and delete existing toggles"
      pageReady={!isFetchingData}>
      <DataGrid
        autoHeight
        style={{ background: 'white' }}
        rows={toggles}
        density="comfortable"
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableSelectionOnClick
        components={{
          Toolbar: () =>
            ActionToolbar({
              permissions,
              onCreate: () => setNewToggleDialogOpen(true),
            }),
        }}
        columns={[
          {
            field: 'id',
            headerName: 'ID',
            width: 100,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => <Avatar>{row.id}</Avatar>,
          },
          {
            field: 'featureName',
            headerName: 'Feature Name',
            sortable: false,
            headerAlign: 'left',
            align: 'left',
            flex: 1,
          },
          {
            field: 'modifiedBy',
            headerName: 'Modified By',
            width: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'modifiedAt',
            headerName: 'Modified At',
            valueGetter: ({ row }) =>
              row.modifiedAt !== '' &&
              moment(row.modifiedAt).format('DD/MM/YYYY - HH:mm'),
            width: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'status',
            name: 'Status',
            width: 120,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => (
              <Switch
                disabled={!permissions?.canEnableDisableTogglz}
                checked={row.status === 'ENABLED'}
                onChange={(event) => statusChangeHandler(event, row)}
              />
            ),
          },
          {
            field: 'actions',
            type: 'actions',
            getActions: ({ row }) => [
              <GridActionsCellItem
                key={row.id}
                icon={<DeleteIcon />}
                label="Delete"
                disabled={!permissions?.canDeleteTogglz}
                onClick={() => deleteHandler(row.id)}
              />,
            ],
          },
        ]}
        pageSize={5}
        rowsPerPageOptions={[5]}
      />
      <ConfirmationDialog
        openDialog={showDeleteModal}
        yesHandler={yesDeleteHandler}
        data={toDeleteToggle}
        title="Delete Toggle"
        message="Are you sure you want to delete the selected Toggle ?"
      />
      <Dialog open={newToggleDialogOpen}>
        <DialogTitle>Add New Toggle</DialogTitle>
        <Form
          onSubmit={createNewToggle}
          initialValues={{}}
          validate={validateNewToggle}
          render={({ submitError, handleSubmit, modifiedSinceLastSubmit }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <DialogContent style={{ minHeight: '185px' }}>
                <DialogContentText>
                  Insert the toggle name that can be referenced from the
                  application level
                </DialogContentText>
                <Box sx={{ marginTop: '10px', minHeight: '80px' }}>
                  <TextField
                    label="Toggle Name"
                    name="featureName"
                    required
                    variant="filled"
                    fullWidth
                  />
                </Box>
                {submitError && !modifiedSinceLastSubmit && (
                  <Alert severity="error">{submitError}</Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setNewToggleDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Submit</Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </ApplicationPageTemplate>
  );
}
